import React, { useState } from "react";
import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./App.css";

// ---- responsive image helper (desktop unchanged) ----
const responsiveImg = (base) => ({
  src: `/assets/optimized/${base}-1600.webp`,
  srcSet: `/assets/optimized/${base}-800.webp 800w, /assets/optimized/${base}-1200.webp 1200w, /assets/optimized/${base}-1600.webp 1600w`,
  sizes: `(max-width: 700px) 100vw, (max-width: 1024px) 90vw, 1600px`,
});

export default function App() {


  /* >>> RM_VIDEO_START */
  useEffect(() => {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const v = document.querySelector('.hero video');
        if (v && !v.paused) v.pause();
      }
    } catch (e) {}
  }, []);
  /* <<< RM_VIDEO_END */


  // Reveal highlights on scroll (adds .in-view once)
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const tiles = Array.from(document.querySelectorAll('.tile'));
    if (!tiles.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in-view');
          io.unobserve(e.target);
        }
      });
    }, { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    tiles.forEach(t => io.observe(t));
    return () => io.disconnect();
  }, []);

  const [workModal, setWorkModal] = useState(null);
  useEffect(() => {
    document.body.classList.toggle("modal-open", Boolean(workModal));
  }, [workModal]);
  const [introOpen, setIntroOpen] = useState(false);

  // --- DATA ---
  const works = [
    {
      title: "Champagne at the End of the Earth",
      place: "South Pole",
      tile: "/assets/unique-antartica.jpg",
      image: "/assets/spb.jpg",
      copy:
        "On New Year’s Eve, a small group of friends stood at the bottom of the world, raising glasses as the Antarctic wind whipped around them. The flight, the bespoke polar gear, even the handwritten menus flown in from Paris - everything perfect. “It's the most alive I have ever felt,” said one guest."
    },
    {
      title: "A Night in Rembrandt’s Kitchen",
      place: "Amsterdam",
      tile: "/assets/as-main.jpg",
      image: "/assets/as-backup.jpg",
      copy:
        "On the eve of their 61st wedding anniversary, they were welcomed into the artist’s former home after the doors had closed to the public. The curator guided them through candlelit rooms, showing works rarely seen outside the archives. In the original kitchen, a celebrated Dutch chef served an exquisite multi course dinner paired with wines chosen to match the era. The air was thick with history, love, and the quiet knowledge that some experiences can never be repeated."
    },
    {
      title: "Midnight Illumination",
      place: "Patagonia",
      tile: "/assets/pats.jpg",
      image: "/assets/patb.jpg",
      copy:
        "In Patagonia’s wild south, an expedition was timed to coincide with a rare atmospheric phenomenon. At midnight, our guests stood in silence as the Southern Lights danced across the glaciers. They returned to camp to find the fires stoked, Malbec poured, and the chef preparing fresh king crab caught that afternoon."
    },
    {
      title: "A Canvas of Endless Horizons",
      place: "Sahara Desert",
      tile: "/assets/dunes.jpg",
      image: "/assets/duneb.jpg",
      copy:
        "On the final night of a Middle Eastern journey, guests were led beyond the dunes to a candlelit table set for two in the shadow of an ancient fortress. Musicians played softly as stars emerged. “We forgot the rest of the world existed,” they said."
    },
    {
      title: "The City Behind Closed Doors",
      place: "Kyoto",
      tile: "/assets/kyts.jpg",
      image: "/assets/kytb-v2.jpeg",
      copy:
        "In Kyoto, the doors to a centuries old tea house opened for our clients, a place normally reserved for political leaders and cultural icons. The evening was a sequence of performances, private tastings, and unguarded conversation with a renowned geisha. “It felt like being written into a story that will be told long after we are gone,” one guest said."
    },
    {
      title: "A Second Date Beyond Imagination",
      place: "Maldives",
      tile: "/assets/fss.jpg",
      image: "/assets/fsb.jpg",
      copy:
        "At one of the world’s most exclusive island hideaways, a couple’s second date became the story of a lifetime. They soared by zipline through the treetops to a private dining platform, where a Michelin starred chef flown in from Italy served a tasting journey, each course a story unfolding on the plate. Afterwards, they wandered barefoot along a starlit beach. As they waded into the lagoon, the water bloomed with bioluminescence, trails of bright blue light spiralling around them with every movement, encircling them like the night itself had conspired to hold them close. And in that moment, they began to fall in love. These are the moments we design, where rarity and romance meet in perfect harmony."
    }
  ];

  const whoWeAre = [
    "We are a private travel office for those who live by the exceptional. Discovery Black began as a personal pursuit of rare experiences, and has grown into a trusted circle for discerning families who value access, precision, and the imagination to create what cannot be found elsewhere.",
    "We operate within the world we design. We live in the destinations we plan. We sit at the right tables. We know the people who open the doors that matter. ",
    "Each journey is crafted with the same care we apply to our own travel, removing barriers and shaping experiences worthy of your time.",
    "No templates. No compromises. Only the exceptional."
  ];

  const footerCopy = [
    "Founded in 2012, Discovery Black was built on the belief that travel at its highest level is both art and craft. We began in New Zealand, curating journeys for a small circle of families. Today our reach is global, yet our principles remain unchanged: listen closely, plan intelligently, execute flawlessly.",
    "We serve only a select number of clients each year, dedicating the time, discretion, and care that genuine luxury requires. Every journey begins on a blank page, shaped with the precision of a master artisan and designed not only around where you wish to go, but how you wish to feel when you are there.",
    "Whether you travel once a year or often, our role is constant: to ensure each trip is extraordinary, unforgettable, and truly your own.",
    "If you have been referred by a trusted client, partner, or family office, welcome."
  ];

  return (
    <div className="site">
      {/* HERO */}
      <section className="hero">
<video
  className="hero-video"
  autoPlay
  muted
  loop
  playsInline
  preload="metadata"
  poster="/assets/optimized/hero-poster.jpg"
>
  <source src="/assets/optimized/hero-1080.webm" type="video/webm" />
  <source src="/assets/optimized/hero-1080.mp4" type="video/mp4" media="(min-width: 768px)" />
  <source src="/assets/optimized/hero-720.mp4"  type="video/mp4" media="(max-width: 767px)" />
</video>
        <div className="hero-overlay" />
        <div className="hero-fade" />
        <div className="hero-content">
          <div className="glassPanel" aria-label="Discovery Black">
            <motion.img
              className="logo"
              src="/assets/db-logo.png"
              alt="Discovery Black"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            />
            <p className="sublead hero-sublead">Bespoke Journeys For A Private Few</p>
            <button className="cta" onClick={() => setIntroOpen(true)}>By Invitation</button>
          </div>

          {/* scroll indicator */}
          <button
            className="scroll-indicator"
            onClick={() => document.getElementById("who")?.scrollIntoView({ behavior: "smooth" })}
            aria-label="Scroll down"
          >
            <span className="chev-wrap">
              <svg viewBox="0 0 24 24" className="chevron" aria-hidden="true">
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <svg viewBox="0 0 24 24" className="chevron second" aria-hidden="true">
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section id="who" className="section who">
        <div className="container whoWrap">
          <div className="whoBackdrop" />
          <div className="whoCard">
            <h2 className="section-title ink">Who we are</h2>
            <div className="rule-teal" />
            {whoWeAre.map((line, i) => (<p key={i}>{line}</p>))}
          </div>
        </div>
      </section>

      {/* WHAT MATTERS MOST (PILLARS) */}
      <section className="pillars">
        <h2 className="pillars-heading">What matters most</h2>
        <div className="rule-teal" />
        <div className="pillars-grid">
          <div className="pillar">
            <div className="pillar-title">
              <span className="t1">Access &</span>
              <span className="t2">Rarity</span>
            </div>
            <div className="pillar-text">
              We grant entry where others are refused. Privileged relationships, cultivated over years and guarded with absolute discretion, position you where only the rarest may stand. These moments are not offered, they are earned.
            </div>
          </div>
          <div className="pillar">
            <div className="pillar-title">
              <span className="t1">Experience &</span>
              <span className="t2">Knowledge</span>
            </div>
            <div className="pillar-text">
              We curate journeys worthy of our own passports. As travellers of equal calibre to our clients, we understand what matters because we insist upon it ourselves. Every recommendation is grounded in experience.
            </div>
          </div>
          <div className="pillar">
            <div className="pillar-title">
              <span className="t1">Precision &</span>
              <span className="t2">Refinement</span>
            </div>
            <div className="pillar-text">
              Every journey is composed with exacting precision. Our meticulous planning shapes each moment with purpose, allowing the experience to unfold effortlessly. From timing to tempo, absolutely nothing is left to chance.
            </div>
          </div>
          <div className="pillar">
            <div className="pillar-title">
              <span className="t1">Trust &</span>
              <span className="t2">Discretion</span>
            </div>
            <div className="pillar-text">
              Every exchange is anchored in honour, respect, and an unbroken tradition of absolute discretion. We guard your time and information as carefully as our own. Trust is earned in moments and upheld over years.
            </div>
          </div>
        </div>
      </section>

      {/* SELECTED HIGHLIGHTS */}
      <section className="section">
        <div className="container header-row">
          <h2 className="section-title">Moments that Matter</h2>
<div className="rule-teal" />

        </div>

        <div className="container grid-4">
          {works.map(w => (
            <div key={w.title + w.place} className="tile" onClick={() => setWorkModal(w)}>
{(() => {
  const base = w.tile.split("/").pop().split(".")[0];
  const { src, srcSet, sizes } = responsiveImg(base);
  return (
    <img
      className="tile-img"
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={w.title}
      loading="lazy"
      decoding="async"
      draggable="false"
    />
  );
})()}
              <div className="tile-grad" />
              <div className="tile-copy">
                <div className="tile-title">
                  <div className="t1">{w.title}</div>
                  <div className="t2">{w.place}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PAPER FOOTER (OUR STORY) */}
      <section className="paper">
        <div className="container paper-grid">
          <div className="copy">
            <h3>Our Story</h3>
            <div className="rule-teal" />
            {footerCopy.map((p, i) => (<p key={i}>{p}</p>))}
          </div>
          <div className="aside">
            <div className="label">Introductions Begin Here</div>
            <div className="email">
              <a className="email-link" href="mailto:concierge@discoveryblack.com">concierge@discoveryblack.com</a>
            </div>
          </div>
        </div>
      </section>

      {/* LEGAL FOOTER */}
      <footer className="footer">
        <div className="container footer-row">
          <div>© {new Date().getFullYear()} Discovery Black</div>
          <div>Private office for travel</div>
        </div>
      </footer>

      {/* WORK MODAL */}
      <AnimatePresence>
        {workModal && (
          <div className="modal" onClick={() => setWorkModal(null)}>
            <motion.div
              className="modal-card"
              onClick={(e) => e.stopPropagation()}
              initial={{opacity:0,y:20}}
              animate={{opacity:1,y:0}}
              exit={{opacity:0,y:10}}
            >
{(() => {
  const base = workModal.image.split("/").pop().split(".")[0];
  const { src, srcSet, sizes } = responsiveImg(base);
  return (
    <img
      className="modal-image"
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={workModal.title}
      loading="lazy"
      decoding="async"
      draggable="false"
    />
  );
})()}
              <div className="modal-body">
                <div className="modal-title">
                  <div className="t1">{workModal.title}</div>
                  <div className="t2">{workModal.place}</div>
                </div>
                <div
                  className="modal-text"
                  dangerouslySetInnerHTML={{
                    __html: workModal.copy
                      .replace(/“([^”]+)”/g, "<em>“$1”</em>")
                      .replace(/"([^"]+)"/g, "<em>“$1”</em>")
                  }}
                />                <button className="close" onClick={() => setWorkModal(null)}>Close</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* INTRO MODAL */}
      <AnimatePresence>
        {introOpen && (
          <div className="intro" onClick={() => setIntroOpen(false)}>
            <motion.div
              className="intro-card"
              onClick={(e) => e.stopPropagation()}
              initial={{opacity:0,y:20}}
              animate={{opacity:1,y:0}}
              exit={{opacity:0,y:10}}
            >
              <div className="intro-head">
                <div className="intro-title">By Invitation</div>
                <button className="intro-close" onClick={() => setIntroOpen(false)}>Close</button>
              </div>
              <div className="intro-body">
                We work by introduction. If a trusted friend or adviser has connected us,
                please email <a className="email-link" href="mailto:concierge@discoveryblack.com">concierge@discoveryblack.com</a>.
                We will reply personally and set a time to talk in a way that suits you.
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
